import { Alert, Button, Item, ItemContent, ItemHeader, ItemTitle, ScrollArea, Skeleton } from '@ibsys/kit';
import { ChevronLeftIcon, ChevronRightIcon, TrashIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import type { KnowledgeNodeInSet } from '@/entities/knowledge-set';
import { SmartSearchInput, useSmartSearchPaginated } from '@/features/smart-search';

type Props = {
  knowledgeSetId?: number;
  isCreating: boolean;
  localNodeIds: Set<number>;
  nodesCache: Map<number, KnowledgeNodeInSet>;
  onRemoveNode: (nodeId: number) => void;
  readonly?: boolean;
};

export const SelectedNodesTab: FC<Props> = ({
  knowledgeSetId,
  isCreating,
  localNodeIds,
  nodesCache,
  onRemoveNode,
  readonly,
}) => {
  const { t } = useTranslation(['knowledgeSets', 'common']);

  const baseFilters = useMemo(
    () => ({
      ...(knowledgeSetId && !isCreating ? { knowledgeSetId } : {}),
      isDeleted: false,
    }),
    [knowledgeSetId, isCreating],
  );

  const {
    searchState,
    items: serverItems,
    page,
    setPage,
    predicateInfo,
    handleSearch,
    handleClear,
    isLoading,
    isSearchAnimating,
    searchError,
  } = useSmartSearchPaginated<'knowledge', KnowledgeNodeInSet>({
    entityType: 'knowledge',
    baseFilters,
    pageSize: 50,
  });

  // Merge server items with cached nodes
  const visibleItems = useMemo(() => {
    const nodeMap = new Map<number, KnowledgeNodeInSet>();
    for (const item of serverItems) {
      if (localNodeIds.has(item.id)) nodeMap.set(item.id, item);
    }
    for (const nodeId of localNodeIds) {
      if (!nodeMap.has(nodeId)) {
        const cached = nodesCache.get(nodeId);
        if (cached) nodeMap.set(nodeId, cached);
      }
    }
    return Array.from(nodeMap.values());
  }, [serverItems, localNodeIds, nodesCache]);

  const paginatedItems = useMemo(() => visibleItems.slice(page * 50, (page + 1) * 50), [visibleItems, page]);
  const totalPaginatedPages = Math.ceil(visibleItems.length / 50);

  if (localNodeIds.size === 0 && !isLoading) {
    return <Alert className="text-muted-foreground p-4" title={t('knowledgeSets:noSelectedNodes')} />;
  }

  if (isLoading && serverItems.length === 0 && nodesCache.size === 0) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full overflow-hidden">
      <SmartSearchInput
        value={searchState.value}
        onChange={searchState.onChange}
        mode={searchState.searchMode}
        onModeChange={searchState.setSearchMode}
        onSearch={handleSearch}
        onClear={handleClear}
        predicateInfo={predicateInfo}
        error={searchError}
        isLoading={isSearchAnimating}
        placeholder={t('knowledgeSets:searchNodesPlaceholder')}
      />

      {paginatedItems.length === 0 && !isLoading ? (
        <Alert className="text-muted-foreground p-4" title={t('knowledgeSets:noSelectedNodes')} />
      ) : (
        <ScrollArea className="flex-1">
          <ul className="flex flex-col gap-2">
            {paginatedItems.map((node) => (
              <li key={node.id}>
                <Item variant="outline" size="sm">
                  <ItemHeader className="flex items-center justify-between">
                    <div className="flex flex-col gap-1 min-w-0 flex-1">
                      <ItemTitle className="text-sm font-medium !w-auto break-words">{node.name}</ItemTitle>
                      <div className="flex items-center gap-2">
                        <ItemContent className="text-xs text-muted-foreground shrink-0">{node.type}</ItemContent>
                        <NavLink to={`/knowledge/${node.id}`} className="text-xs text-primary hover:underline shrink-0">
                          {t('knowledgeSets:goToNode')}
                        </NavLink>
                      </div>
                    </div>
                    {!readonly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onRemoveNode(node.id)}
                        aria-label={t('knowledgeSets:removeFromSet')}
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    )}
                  </ItemHeader>
                </Item>
              </li>
            ))}
          </ul>
        </ScrollArea>
      )}

      {totalPaginatedPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-2">
          <Button
            variant="outline"
            size="icon"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            aria-label="Previous page"
          >
            <ChevronLeftIcon className="size-4" />
          </Button>
          <span className="text-sm text-muted-foreground">{`${page + 1} / ${totalPaginatedPages}`}</span>
          <Button
            variant="outline"
            size="icon"
            disabled={page >= totalPaginatedPages - 1}
            onClick={() => setPage((p) => p + 1)}
            aria-label="Next page"
          >
            <ChevronRightIcon className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
